'use client';

/**
 * Mounts the offline write-queue drain and reports anything it had to abandon
 * (audit finding OFF-01).
 *
 * `useBackgroundSync` was previously never mounted anywhere in the app, so the
 * Dexie `sync_queue` was never drained — offline writes were promised
 * ("Changes will sync when you reconnect") and then silently discarded. This
 * provider is rendered inside `QueryProvider`, so the drain runs for the whole
 * authenticated app, and any write that genuinely cannot be replayed gets a
 * visible card with Retry / Dismiss instead of disappearing.
 */

import { useBackgroundSync, describeSyncAction } from '@/lib/hooks/use-background-sync';
import { Button } from '@write4me/ui/components/button';

export function BackgroundSyncProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { failedItems, retryItem, discardItem } = useBackgroundSync();

  return (
    <>
      {children}

      {failedItems.length > 0 && (
        <div
          className="fixed bottom-4 right-4 z-[70] flex max-w-sm flex-col gap-2"
          role="alert"
          aria-live="polite"
        >
          {failedItems.map((item) => (
            <div
              key={item.id}
              className="rounded-[12px] border border-error/20 bg-surface p-4 shadow-card"
            >
              <p className="font-ui text-sm font-medium text-ink">
                Couldn&apos;t sync: {describeSyncAction(item.action)}
              </p>
              <p className="mt-1 font-ui text-xs text-ink-muted">
                {item.last_error ??
                  'This change made while offline could not be saved.'}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => item.id !== undefined && void retryItem(item.id)}
                >
                  Retry
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() =>
                    item.id !== undefined && void discardItem(item.id)
                  }
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
